import type { JSX } from 'react';
import { useI18n } from '../../i18n';
import type { I18n } from '../../i18n';
import { displayable } from '../../lib/config';
import { ipOf } from '../../lib/ipInfo';
import { networkTitleKey } from '../../lib/networkStatus';
import type { NetStatus } from '../../lib/networkStatus';
import { cx } from './cx';
import { Icon, type IconName } from './Icon';
import { Module, ModuleDivider } from './Module';

/**
 * Everything the interface says about the network — a port of
 * `android/.../ui/components/NetworkStatusUi.kt` (the bar and the banner) and
 * `StatusStrip.kt` (`displayTitle`, `icon`, `ConnectionModule`).
 *
 * ### The one substitution, and it runs through this whole file
 *
 * Android's `NetworkStatus` carries a transport (`wifi` / `mobile`), a cellular
 * generation (`4g` / `5g`) and a `validated` flag that separates "connected" from
 * "connected to a hotel wifi that goes nowhere". Its `displayTitle()` prints
 * "Wi-Fi", "5G" or `net_no_internet` from those. **Safari on iOS gives
 * JavaScript none of it** — there is no Network Information API there, no
 * `navigator.connection`, and no substitute (SPEC §14 #5). So `NetStatus` has
 * exactly one field and the display can only say Connected or Offline.
 *
 * Printing "Wi-Fi" here would be a guess rendered in the same slot Android
 * renders a measurement into, and `network` is one of the fields that labels
 * every submitted batch — a plausible wrong value is indistinguishable from a
 * measured one once it reaches the backend. Same rule as `statusCode` staying
 * null: never fabricate a field.
 *
 * Two consequences worth naming so nobody "restores" them later:
 *
 * - `net_no_internet` and `net_connected_no_internet_body` are not ported. They
 *   describe the connected-but-no-uplink state, which cannot be detected here.
 * - `networkIcon` returns the generic `wifi` glyph for *any* connection, not
 *   because the device is on wifi but because that glyph is the one the icon set
 *   uses for connectivity-in-general. `network-cell` exists in `Icon.tsx` for
 *   parity with Android's set and is deliberately unreachable from here.
 *
 * What the carrier, region and IP shown below *are* trustworthy about is
 * unchanged from Android: they come from **ipwho.is**, the real egress IP, never
 * from the SIM. A no-signal SIM keeps stale operator names that flip between
 * carriers, so the SIM is the wrong source even on the platform where it can be
 * read at all. `displayable` and `ipOf` are imported rather than written here:
 * the rule that keeps the `UNKNOWN` wire sentinel off the screen is tested in
 * `src/lib`, and it used to exist as two copies that had already drifted.
 */

/**
 * The label for the current connection. Port of `NetworkStatus.displayTitle()`,
 * collapsed to the two outcomes this platform can actually distinguish.
 *
 * Takes `t` rather than calling `useI18n()` so it stays a plain function: the
 * bar, the module and the Settings device list all need this string, and one of
 * those wants it as a value inside a `LabelValueRow` rather than as a component.
 * The choice of *which* string is `networkTitleKey`, in `src/lib` beside the
 * type it reads; all that happens here is the translation, because nothing in
 * `src/lib` holds user-facing text.
 */
export function networkDisplayTitle(status: NetStatus, t: I18n['t']): string {
  return t(networkTitleKey(status));
}

/** Port of `NetworkStatus.icon()`. See the file note on why online is generic. */
export function networkIcon(status: NetStatus): IconName {
  return status.online ? 'wifi' : 'signal-wifi-off';
}

/**
 * Full-width status bar for the key-entry gate.
 *
 * Android tints this bar with `colorScheme.primary` when connected. That is not
 * portable as written: Color.kt is explicit that the electric blue carries the
 * primary action "as a fill, never as text", and this bar's title *is* text at
 * 14px — 3.84:1, short of the 4.5:1 a word needs. The connected state uses
 * `--secondary` (cyan, 10.6:1) and its 10% wash instead. Same palette, and the
 * guarantee Android's `ContrastTest` makes survives the port.
 *
 * The title is all it shows. Android's bar can append the carrier; here the bar
 * is on the key-entry gate, which runs before any cycle has snapshotted one —
 * so there was never anything to pass, and the slot has been removed rather than
 * left as a branch nothing reaches. The carrier does appear once the app is
 * past the gate, in {@link ConnectionModule}.
 */
export function NetworkStatusBar({
  status,
  className,
}: {
  status: NetStatus;
  className?: string;
}): JSX.Element {
  const { t } = useI18n();
  return (
    <div
      className={cx('net-bar', status.online ? 'net-bar--online' : 'net-bar--offline', className)}
    >
      <Icon name={networkIcon(status)} size={18} className="net-bar__icon" />
      <span className="net-bar__title t-title-small">{networkDisplayTitle(status, t)}</span>
    </div>
  );
}

/**
 * Warning banner for a device with no network.
 *
 * **Renders `null` when online**, so callers drop it in unconditionally rather
 * than each deciding for themselves — the Kotlin returns early for the same
 * reason. Three screens show this; three copies of the same `if` is three
 * chances for one of them to be missing when it matters.
 *
 * Android picks between two bodies here on `status.kind == null`. Only
 * `net_no_connection_body` is reachable on this platform — see the file note.
 */
export function NoInternetBanner({ status }: { status: NetStatus }): JSX.Element | null {
  const { t } = useI18n();
  if (status.online) return null;
  return (
    <div className="no-internet">
      <Icon name="warning" size={18} className="no-internet__icon" />
      <p className="no-internet__text t-body-medium">{t('net_no_connection_body')}</p>
    </div>
  );
}

/**
 * Where this device is connected: carrier, connection state, region and public
 * IP. Port of `StatusStrip.kt#ConnectionModule`, and it keeps Android's shape —
 * a transport icon with the carrier as the headline and the status underneath,
 * a divider, then Region and IP as wrapping cells.
 *
 * The carrier gets the headline because it is the field an operator checks
 * first when a result looks wrong: it answers "is this device on the network I
 * think it is".
 *
 * `carrier` and `region` arrive already extracted, because that is the shape the
 * cycle stores them in — `MeasurementContext` snapshots them as plain strings at
 * the *start* of a run and keeps only the raw ipwho.is object alongside for the
 * backend. Re-parsing that object here would show the network as it is now
 * rather than the one the batch on screen was measured on.
 */
export function ConnectionModule({
  status,
  ipInfo,
  carrier,
  region,
}: {
  status: NetStatus;
  ipInfo: Record<string, unknown> | null;
  carrier?: string | null;
  region?: string | null;
}): JSX.Element {
  const { t } = useI18n();
  // Android animates between alive and warn with `animateColorAsState`. Here it
  // is a class swap: `--value-color` is what the CSS reads, and a CSS
  // transition on a custom property does not interpolate in Safari. The state
  // changes at most once every few minutes, so there is nothing to smooth.
  const tint = status.online ? 'c-alive' : 'c-warn';
  const unknown = t('value_unknown');

  return (
    <Module>
      <div className="module__row">
        <Icon
          name={networkIcon(status)}
          size={28}
          className={cx('connection-module__icon', tint)}
        />
        <div className="connection-module__text">
          <div className="connection-module__isp t-title-medium clamp-2 break-anywhere">
            {displayable(carrier) ?? unknown}
          </div>
          <div className={cx('connection-module__net t-body-medium', tint)}>
            {networkDisplayTitle(status, t)}
          </div>
        </div>
      </div>

      <ModuleDivider />

      <div className="connection-cells">
        <ConnectionCell label={t('device_region')} value={displayable(region) ?? unknown} />
        <ConnectionCell label={t('device_ip')} value={ipOf(ipInfo) ?? unknown} numeric />
      </div>
    </Module>
  );
}

/**
 * One labelled fact. Width is capped in CSS rather than fixed: a cell allowed to
 * grow without limit pushes its neighbour onto the next line even when there was
 * room for both.
 */
function ConnectionCell({
  label,
  value,
  numeric = false,
}: {
  label: string;
  value: string;
  numeric?: boolean;
}): JSX.Element {
  return (
    <div className="connection-cell">
      <span className="connection-cell__label t-label-small clamp-1">{label}</span>
      <span className={cx('connection-cell__value clamp-2 break-anywhere', numeric ? 'n-small' : 't-body-medium')}>
        {value}
      </span>
    </div>
  );
}
